<template>
  <view-page
    :title="title"
    :loading="loading"
    :delete-dialog-title="$t('DeleteTicket')"
    :delete-dialog-content="deleteMessage"
    :delete-button="userHasPermissionTo('destroy')"
    :edit-button="userHasPermissionTo('update')"
    @editItem="editItem"
    @deleteItem="deleteTicket"
    :permission-name="permissionName"
  >
    <template v-slot:leftSide>
      <view-page-data-field
        v-for="field in fields"
        :key="field.template"
        :icon="field.icon"
        :label="field.label"
        :data="field.data"
      />
    </template>

    <template v-slot:headerRightSide>
      <div>
        <management-tabs-and-search
          :tabs="tabs"
          :open-tab="openTab"
          @update-open-tab="openTab = $event"
        />
        <management-add-button
          v-if="openTab === 'comments'"
          :label="$t('AddComment')"
          @buttonClick="createComment"
          :disabled="
            !userHasManagementPermission(
              'third_party_apis_youtrack_comments',
              'store'
            )
          "
          :list-missing-permissions="[]"
        />
        <management-add-button
          v-if="openTab === 'attachments'"
          :label="$t('AddAttachment')"
          @buttonClick="openAttachmentForm"
          :disabled="false"
          :list-missing-permissions="[]"
        />
      </div>
    </template>

    <template v-slot:rightSide>
      <v-tabs-items
        :value="openTab"
        v-if="ticket"
        :key="ticket.comments.length"
      >
        <v-tab-item value="description" class="elevation-2">
          <textarea
            v-model="ticket.description"
            id="description"
            class="input-field"
            readonly
            style="height: 379px"
          />
        </v-tab-item>
        <v-tab-item value="comments">
          <ticket-comments-table
            :ticketId="ticket.id"
            @open-comment="openComment"
          />
        </v-tab-item>
        <v-tab-item value="attachments">
          <attachments-table
            :attachments="ticket.attachments"
            @open-attachment="openAttachment($event)"
            @reload-ticket="getTicket"
          />
        </v-tab-item>
      </v-tabs-items>
      <attachment-form
        :open="attachmentForm.isOpen"
        @add-attachment="saveAttachment($event)"
        @close="closeAttachmentForm"
      />
    </template>
  </view-page>
</template>

<script>
import { mapActions } from "vuex";
import ViewPage from "@/components/Management/ViewPage/ViewPage";
import ViewPageDataField from "@/components/Management/ViewPage/ViewPageDataField";
import ManagementTabsAndSearch from "@/components/_Organisms/ManagementTabsAndSearch";
import TicketCommentsTable from "@/components/Support/TechnicalSupport/TicketCommentsTable";
import AttachmentsTable from "@/components/Support/TechnicalSupport/AttachmentsTable";
import AttachmentForm from "@/components/Support/TechnicalSupport/AttachmentForm";
import ManagementAddButton from "@/components/Management/ManagementAddButton";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";

export default {
  name: "TicketDetails",

  components: {
    ViewPageDataField,
    ManagementTabsAndSearch,
    ViewPage,
    TicketCommentsTable,
    ManagementAddButton,
    AttachmentsTable,
    AttachmentForm
  },

  mixins: [managementDatatableMixin, checkPermissionsMixin],

  data() {
    return {
      ticket: null,
      loading: false,
      openTab: "description",
      fields: null,
      attachmentForm: {
        isOpen: false
      },

      permissionName: "third_party_apis_youtrack_issues"
    };
  },

  computed: {
    ticketID() {
      return this.$route.params.id;
    },

    title() {
      return this.ticket ? this.ticket.idReadable : this.$i18n.tc("Ticket", 1);
    },

    tabs() {
      return [
        {
          value: "description",
          text: this.$i18n.t("Description"),
          hasSearch: false
        },
        {
          value: "comments",
          text: this.$i18n.tc("Comment", 2),
          hasSearch: true
        },
        {
          value: "attachments",
          text: this.$i18n.tc("Attachment", 2),
          hasSearch: true
        }
      ];
    },

    deleteMessage() {
      return this.ticket
        ? this.$i18n.t("DeleteConfirmation") +
            " " +
            this.ticket.idReadable +
            "?"
        : "";
    }
  },

  watch: {
    ticketID() {
      this.getTicket();
    }
  },

  mounted() {
    this.getTicket();
  },

  methods: {
    ...mapActions([
      "fetchTicketByID",
      "deleteTicketByID",
      "createTicketAttachment"
    ]),

    async getTicket() {
      if (this.ticketID) {
        this.loading = true;
        try {
          this.ticket = await this.fetchTicketByID(this.ticketID);

          if (
            this.ticket.error === "Not Found" ||
            this.ticket.deleted === 1 ||
            this.ticket.visibility === "private"
          ) {
            this.$router.push("/error");
          }
        } catch (e) {
          console.error(e);
        } finally {
          this.loading = false;
          this.fields = this.getFields();
        }
      }
    },

    async deleteTicket() {
      try {
        if (this.userHasPermissionTo("destroy")) {
          await this.deleteTicketByID(this.ticket.id);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.$router.back();
      }
    },

    async saveAttachment(newAttachment) {
      this.loading = true;
      let upload = [];
      let formData = new FormData();

      if (newAttachment instanceof File) {
        upload.push(newAttachment);
      } else {
        upload = Array.from(newAttachment);
      }

      for (let i = 0; i < upload.length; i++) {
        formData.append("upload[]", upload[i]);
      }

      try {
        this.ticket = await this.createTicketAttachment({
          formData,
          ticketID: this.ticketID
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
        this.getTicket();
      }
    },

    editItem() {
      this.$router.push({
        name: "tickets-edit",
        params: { uuid: this.ticketID }
      });
    },

    getFields() {
      if (this.ticket) {
        return [
          {
            icon: "fingerprint",
            label: this.$i18n.tc("ID", 1),
            data: this.ticket.idReadable
          },
          {
            icon: "title",
            label: this.$i18n.t("Title"),
            data: this.ticket.summary
          },
          {
            icon: "priority_high",
            label: this.$i18n.t("Priority"),
            data: this.ticket.priority
          },
          {
            icon: "pending_actions",
            label: this.$i18n.t("State"),
            data: this.ticket.state
          },
          {
            icon: "person",
            label: this.$i18n.t("CreatedBy"),
            data: this.ticket.fibercloud_user_name
          },
          {
            icon: "event",
            label: this.$i18n.t("CreationDate"),
            data: this.ticket.created
          }
        ];
      }
      return null;
    },

    openAttachmentForm() {
      this.attachmentForm.isOpen = true;
    },

    closeAttachmentForm() {
      this.attachmentForm.isOpen = false;
    },

    openComment(comment) {
      if (
        this.userHasManagementPermission(
          "third_party_apis_youtrack_comments",
          "show"
        )
      ) {
        this.$router.push({
          name: "comment-view",
          params: {
            commentId: comment.id
          }
        });
      }
    },

    createComment() {
      if (
        this.userHasManagementPermission(
          "third_party_apis_youtrack_comments",
          "store"
        )
      ) {
        this.$router.push({
          name: "comments-create",
          params: {
            ticketId: this.ticketID
          }
        });
      }
    },

    openAttachment(attachment) {
      window.open(attachment.url);
    }
  }
};
</script>

<style scoped>
#description {
  border: transparent;
  padding: 20px;
  cursor: inherit;
  height: 419px;
}

.slide-fade-enter-active {
  transition: all 0.2s ease-in;
}
.slide-fade-leave-active {
  transition: all 0.1s ease-out;
}
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/Support/TechnicalSupport/TicketsDetails.vue